'use client';

import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, Check, Globe, Palette, Pencil } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { allowedComicPageCounts, comicPageOptionsFromCounts } from '@/lib/pricing/page-length-policy';
import { useSiteFlags } from '@/context/SiteFlagsContext';
import { getResolvedStoryPageCounts, loadStoryPageCounts } from '@/lib/pricing/story-page-counts-client';
import { comicLanguageOptions } from './preview-payload';
import { useToast } from '@/hooks/use-toast';
import { guardCast, typedRelativesNotInPhoto, type TypedRelativesChoice } from '@/utils/manga/family-guard';
import type { MangaCharacterDraft } from './types';
import { TypedRelativeNotice, useTypedRelativeGate } from './typed-relative-gate';
import { IdeaCardPicker } from '@/components/create-flow/IdeaCardPicker';
import {
  fillComicSeed, ideaCardEvents, ideaCardsFor, initialIdeaPick, type FilledIdea, type IdeaOccasion, type IdeaPick,
} from '@/lib/catalogue/idea-cards';
import type { ComicSeed } from '@/lib/catalogue/comic-seeds';
import { inferReaderBand } from '@/utils/manga/comic-story-profile';
import { trackCreate } from '@/utils/create-flow-analytics';
import { FORMAT_COPY } from '@/lib/create-flow/format-copy';
import {
  MangaColorMode,
  MangaReadingDirection,
  MangaSettings,
  MangaSubStyle,
  MANGA_SUB_STYLE_INFO,
  COMIC_PAGE_COUNTS,
  type ComicPageCount,
  comicTradition,
} from '@/types/manga';

interface MangaStyleStepProps {
  settings: MangaSettings;
  onSettingsChange: (settings: MangaSettings) => void;
  storyTitle: string;
  onStoryTitleChange: (title: string) => void;
  storyIdea: string;
  onStoryIdeaChange: (idea: string) => void;
  /** Story language code; the comic's balloons and narration are written in it. */
  language: string;
  onLanguageChange: (language: string) => void;
  onBack: () => void;
  onNext: () => void;
  /** The people step's drafts: the idea is checked against them for relatives not in the photo (Task C3). */
  characters?: MangaCharacterDraft[];
  /** The parent's answer to the typed-relative notice, held by the page. */
  typedRelatives?: TypedRelativesChoice | null;
  onTypedRelativesChange?: (choice: TypedRelativesChoice | null) => void;
  /** "Use a different photo": back to the photo step. */
  onChangePhoto?: () => void;
  /** The idea card that filled the title and idea, held by the flow (null: the parent's own text or none). */
  ideaPick?: IdeaPick | null;
  onIdeaPickChange?: (pick: IdeaPick | null) => void;
  /** ?idea= from the start link: a comic idea id or 'surprise'. */
  pinnedIdea?: string;
  /** ?occasion= from the start link: its ideas come first. */
  occasion?: IdeaOccasion;
  /** The last pick + text whose kept/edited events were sent (held by the flow so Back then Next does not repeat them). */
  ideaEventsKey?: string | null;
  onIdeaEventsSent?: (key: string) => void;
  /**
   * The catalogue story chosen with ?story= (stories-as-comics Task 3): it fills the title, the idea, the style and the
   * length, and locks the length while it is chosen. null/absent: the idea cards as before.
   */
  story?: ComicSeed | null;
  /** The story text last filled in (held by the flow), so an unedited fill is re-filled after Back with new names. */
  storyPick?: IdeaPick | null;
  onStoryPickChange?: (pick: IdeaPick | null) => void;
  /** "Make up your own instead": the flow forgets the story. */
  onClearStory?: () => void;
  /** Shown under the idea field (the picture-book-words notice). */
  ideaNotice?: React.ReactNode;
}

/**
 * The relatives the parent's idea names who are not in the photo. The idea card's text is the baseline, so our own
 * words never raise the typed-relative notice; a relative the parent typed still counts.
 */
export function comicRelativeWords(idea: string, characters: MangaCharacterDraft[], pick: IdeaPick | null): string[] {
  return typedRelativesNotInPhoto(idea, guardCast(characters), pick ? { baseline: pick.idea } : {});
}

const SUB_STYLE_EMOJI: Record<MangaSubStyle, string> = {
  shonen: '⚡',
  shojo: '🌸',
  chibi: '🎈',
  kodomo: '🐣',
  superhero: '🦸',
  cartoon: '😄',
  european: '🗺️',
};

const TRADITION_SECTIONS: Array<{ tradition: 'manga' | 'western'; label: string }> = [
  { tradition: 'manga', label: 'Manga' },
  { tradition: 'western', label: 'Comic Book' },
];

/** The length to fall back to once geo has resolved: the current one when offered (24 counts when square is on), else 16. */
export function pageCountAfterGeo(pageCount: number, tallCounts: number[], squareOn: boolean): number {
  return comicPageOptionsFromCounts(tallCounts, squareOn).some((o) => o.pageCount === pageCount) ? pageCount : 16;
}

export function MangaStyleStep({
  settings,
  onSettingsChange,
  storyTitle,
  onStoryTitleChange,
  storyIdea,
  onStoryIdeaChange,
  language,
  onLanguageChange,
  onBack,
  onNext,
  characters = [],
  typedRelatives = null,
  onTypedRelativesChange = () => {},
  onChangePhoto = onBack,
  ideaPick = null,
  onIdeaPickChange = () => {},
  pinnedIdea,
  occasion,
  ideaEventsKey = null,
  onIdeaEventsSent = () => {},
  story = null,
  storyPick = null,
  onStoryPickChange = () => {},
  onClearStory = () => {},
  ideaNotice,
}: MangaStyleStepProps) {
  const { toast } = useToast();
  const { comicSquare } = useSiteFlags();
  // The chosen story filled with this family's names: our text, so it is the typed-relative baseline
  const seeded = story ? fillComicSeed(story, characters) : null;
  const relativeWords = comicRelativeWords(storyIdea, characters, seeded ?? ideaPick);
  const gate = useTypedRelativeGate({
    flow: 'comic',
    words: relativeWords,
    choice: typedRelatives,
    onChoiceChange: onTypedRelativesChange,
    onChangePhoto,
  });

  // Offered lengths depend on the visitor's country (8 pages only in low-price countries). The geo answer is cached at
  // module level; until it resolves we show only 16 and correct nothing.
  const comicCounts = (counts: number[]): ComicPageCount[] => COMIC_PAGE_COUNTS.filter((n) => counts.includes(n));
  const [allowedCounts, setAllowedCounts] = useState<ComicPageCount[]>(() => {
    const resolved = getResolvedStoryPageCounts();
    return resolved ? comicCounts(resolved) : allowedComicPageCounts(null);
  });
  const [geoResolved, setGeoResolved] = useState(() => getResolvedStoryPageCounts() !== null);
  // The 24-page square book is offered in every country when the flag is on; the server derives the shape from the count.
  // A chosen story locks the length to its own (owner decision: no other cut of a catalogue story)
  const pageOptions = story
    ? [{ pageCount: story.pageCount, shape: story.square ? ('square' as const) : ('tall' as const) }]
    : comicPageOptionsFromCounts(allowedCounts, comicSquare);

  useEffect(() => {
    let cancelled = false;
    loadStoryPageCounts().then((counts) => {
      if (cancelled) return;
      setAllowedCounts(comicCounts(counts));
      setGeoResolved(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Once geo has resolved, switch a length that is not offered here to 16 so form and server agree.
  useEffect(() => {
    if (geoResolved && pageCountAfterGeo(settings.pageCount, allowedCounts, comicSquare) !== settings.pageCount) {
      onSettingsChange({ ...settings, pageCount: 16 });
    }
  }, [geoResolved, allowedCounts, comicSquare, settings.pageCount]);

  const update = (patch: Partial<MangaSettings>) =>
    onSettingsChange({ ...settings, ...patch });

  // Idea cards: ordered by the style chosen and the card picked when the step opened (a later style tap or card tap
  // does not reshuffle them). Pinning the current pick keeps it listed after Back when the family changed (M1).
  const [orderStyle] = useState(() => settings.subStyle);
  const [orderPin] = useState(() => ideaPick?.id ?? pinnedIdea);
  const [editingIdea, setEditingIdea] = useState(false);
  // Set by "Edit the idea": the textarea that replaces the button takes the focus
  const [focusIdea, setFocusIdea] = useState(false);
  const cards = ideaCardsFor({
    product: 'comic',
    cast: characters,
    band: inferReaderBand(characters),
    occasion,
    subStyle: orderStyle,
    pinned: orderPin,
  });

  const applyPick = (pick: IdeaPick) => {
    onStoryTitleChange(pick.title);
    onStoryIdeaChange(pick.idea);
    onIdeaPickChange(pick);
    setEditingIdea(false);
    setFocusIdea(false);
  };

  /** The story's text, style and length (an empty step), or just its text re-filled with the current names. */
  const applyStory = (fill: IdeaPick, withSettings: boolean) => {
    onStoryTitleChange(fill.title);
    onStoryIdeaChange(fill.idea);
    onStoryPickChange(fill);
    if (withSettings && story) {
      update({
        subStyle: story.subStyle,
        colorMode: story.colorMode,
        pageCount: story.pageCount,
        ...(comicTradition(story.subStyle) === 'western' ? { readingDirection: 'ltr' as MangaReadingDirection } : {}),
      });
    }
  };

  // No blank page: an empty step gets a card (?idea, a random one for 'surprise', else the first); an unedited card
  // is filled again with the current names; the parent's own text is never touched. A chosen story does the same
  // with its own text instead of a card.
  useEffect(() => {
    if (story && seeded) {
      if (!storyTitle.trim() && !storyIdea.trim()) {
        applyStory(seeded, true);
      } else if (storyPick && (storyPick.idea !== seeded.idea || storyPick.title !== seeded.title)) {
        // Back, and the names changed: re-fill whichever of our texts the parent left unedited
        if (storyIdea === storyPick.idea) onStoryIdeaChange(seeded.idea);
        if (storyTitle === storyPick.title) onStoryTitleChange(seeded.title);
        onStoryPickChange(seeded);
      }
      return;
    }
    const pick = initialIdeaPick({ title: storyTitle, idea: storyIdea, pick: ideaPick, cards, pinned: orderPin });
    if (pick && (pick.id !== ideaPick?.id || pick.title !== storyTitle || pick.idea !== storyIdea)) applyPick(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickCard = (c: FilledIdea) => applyPick({ id: c.id, title: c.title, idea: c.idea });

  const surprise = () => {
    const others = cards.filter((c) => c.id !== ideaPick?.id);
    if (!others.length) return;
    pickCard(others[Math.floor(Math.random() * others.length)]);
    trackCreate('comic', 'idea_shuffled');
  };

  // "Make up your own instead": our seeded text gives way to the first idea card; text the parent edited stays
  const clearStory = () => {
    const ours = !!seeded && storyIdea === seeded.idea;
    onClearStory();
    onStoryPickChange(null);
    if (!ours) return;
    const pick = initialIdeaPick({ title: '', idea: '', pick: null, cards, pinned: orderPin });
    if (pick) applyPick(pick);
  };

  const ideaCollapsed = (seeded ? storyIdea === seeded.idea : !!ideaPick && storyIdea === ideaPick.idea) && !editingIdea;

  /** `chosen`: the parent just answered the typed-relative notice for these words. */
  const handleNext = (chosen = false) => {
    if (!storyTitle.trim()) {
      toast({
        title: 'Story title required',
        description: 'Give your comic a title before we write the story.',
        variant: 'destructive',
      });
      return;
    }
    if (!chosen && !gate.allowsNext()) return;
    // Once per pick and final text: Back then Next with nothing changed does not count twice
    const eventsKey = ideaPick ? `${ideaPick.id}
${storyTitle}
${storyIdea}` : null;
    if (eventsKey && eventsKey !== ideaEventsKey) {
      for (const e of ideaCardEvents(ideaPick, { title: storyTitle, idea: storyIdea })) {
        trackCreate('comic', e.name, { field: e.field, source: e.source });
      }
      onIdeaEventsSent(eventsKey);
    }
    onNext();
  };

  const toggleButtonClass = (active: boolean) =>
    `flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors border ${
      active
        ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
    }`;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Comic Style &amp; Story</h2>
        <p className="text-gray-600 max-w-xl mx-auto">
          Pick a look and a story idea. We&apos;ve filled in a story to start you off; change anything you like.
        </p>
      </div>

      {/* Sub-style cards, grouped by tradition */}
      <div className="space-y-4">
        {TRADITION_SECTIONS.map(({ tradition, label }) => (
          <div key={tradition}>
            <Label className="flex items-center mb-2 text-gray-700">
              <Palette className="w-4 h-4 mr-2" />
              {label} styles
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(Object.keys(MANGA_SUB_STYLE_INFO) as MangaSubStyle[])
                .filter((key) => MANGA_SUB_STYLE_INFO[key].tradition === tradition)
                .map((key) => {
                  const info = MANGA_SUB_STYLE_INFO[key];
                  const selected = settings.subStyle === key;
                  const select = () =>
                    update({
                      subStyle: key,
                      // Western comics always read left-to-right
                      ...(comicTradition(key) === 'western'
                        ? { readingDirection: 'ltr' as MangaReadingDirection }
                        : {}),
                    });
                  return (
                    <Card
                      key={key}
                      role="button"
                      tabIndex={0}
                      onClick={select}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          select();
                        }
                      }}
                      className={`cursor-pointer transition-all border-2 ${
                        selected
                          ? 'border-purple-500 bg-purple-50 shadow-md'
                          : 'border-gray-200 hover:border-purple-200 hover:shadow-sm'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-xl" aria-hidden>
                              {SUB_STYLE_EMOJI[key]}
                            </span>
                            <span className="font-semibold text-gray-800">{info.name}</span>
                          </div>
                          {selected && (
                            <span className="bg-purple-600 rounded-full p-1">
                              <Check className="w-3 h-3 text-white" />
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{info.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {seeded ? (
        <div data-chosen-story="" className="space-y-1 rounded-lg border-2 border-purple-500 bg-purple-50 p-4">
          <p className="text-sm text-gray-600">Your story</p>
          <p className="font-semibold text-gray-800">{seeded.title}</p>
          <button
            type="button"
            onClick={clearStory}
            className="inline-flex min-h-11 items-center rounded-sm text-sm font-medium text-purple-700 underline underline-offset-4 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep"
          >
            Make up your own instead
          </button>
        </div>
      ) : (
        <IdeaCardPicker cards={cards} selectedId={ideaPick?.id ?? null} onPick={pickCard} onSurprise={surprise} />
      )}

      {/* Color mode + reading direction */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block text-gray-700">Color</Label>
          <div className="flex gap-2">
            <button
              type="button"
              className={toggleButtonClass(settings.colorMode === 'full-color')}
              onClick={() => update({ colorMode: 'full-color' as MangaColorMode })}
            >
              Full color
            </button>
            <button
              type="button"
              className={toggleButtonClass(settings.colorMode === 'screentone')}
              onClick={() => update({ colorMode: 'screentone' as MangaColorMode })}
            >
              B&amp;W screentone
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {comicTradition(settings.subStyle) === 'western'
              ? 'Black & white gives a classic inked-comic look.'
              : 'Screentone is the classic black & white manga look.'}
          </p>
        </div>

        {comicTradition(settings.subStyle) === 'manga' && (
          <div>
            <Label className="mb-2 block text-gray-700">Reading direction</Label>
            <div className="flex gap-2">
              <button
                type="button"
                className={toggleButtonClass(settings.readingDirection === 'ltr')}
                onClick={() => update({ readingDirection: 'ltr' as MangaReadingDirection })}
              >
                Left to right
              </button>
              <button
                type="button"
                className={toggleButtonClass(settings.readingDirection === 'rtl')}
                onClick={() => update({ readingDirection: 'rtl' as MangaReadingDirection })}
              >
                Right to left
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Right-to-left is how manga is read in Japan.
            </p>
          </div>
        )}
      </div>

      {/* Page count */}
      <div>
        <Label className="mb-2 block text-gray-700">Pages</Label>
        {pageOptions.length === 1 ? (
          <p className="text-sm font-medium text-gray-700">
            {pageOptions[0].shape === 'square' ? `${pageOptions[0].pageCount} pages · square` : `${pageOptions[0].pageCount} pages`}
          </p>
        ) : (
        <div className="flex gap-2 max-w-md">
          {pageOptions.map(({ pageCount: count, shape }) => (
            <button
              key={count}
              type="button"
              className={toggleButtonClass(settings.pageCount === count)}
              onClick={() => update({ pageCount: count })}
            >
              {shape === 'square' ? `${count} pages · square` : `${count} pages`}
            </button>
          ))}
        </div>
        )}
      </div>

      {/* Story title + idea */}
      <div className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="manga-language" className="flex items-center text-gray-700">
            <Globe className="w-4 h-4 mr-2" />
            Story language
          </Label>
          <Select value={language} onValueChange={onLanguageChange}>
            <SelectTrigger id="manga-language" className="w-full">
              <SelectValue placeholder="Select a language" />
            </SelectTrigger>
            <SelectContent>
              {comicLanguageOptions().map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">Speech balloons and narration are written in this language. Comics are available in these languages for now. More are coming.</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="manga-story-title" className="flex items-center text-gray-700">
            <BookOpen className="w-4 h-4 mr-2" />
            Story title <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="manga-story-title"
            value={storyTitle}
            placeholder="e.g. Mia and the Thunder Dragon"
            maxLength={80}
            onChange={(e) => onStoryTitleChange(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          {ideaCollapsed ? (
            // Collapsed: no textarea yet, so the label is plain text that names the summary group
            <p id="manga-story-idea-label" className="text-sm font-medium leading-none text-gray-700">
              Story idea <span className="text-gray-400 font-normal">(optional)</span>
            </p>
          ) : (
            <Label htmlFor="manga-story-idea" className="text-gray-700">
              Story idea <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
          )}
          {ideaCollapsed ? (
            <div role="group" aria-labelledby="manga-story-idea-label" className="space-y-2">
              <p data-idea-summary="" className="text-sm text-gray-700">{storyIdea}</p>
              <button
                type="button"
                onClick={() => {
                  setEditingIdea(true);
                  setFocusIdea(true);
                }}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-sm text-sm font-medium text-purple-700 underline underline-offset-4 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep"
              >
                <Pencil className="w-4 h-4" aria-hidden="true" />
                Edit the idea
              </button>
            </div>
          ) : (
            <Textarea
              id="manga-story-idea"
              value={storyIdea}
              rows={3}
              // Only after "Edit the idea": the button that had the focus is gone, so the textarea takes it
              autoFocus={focusIdea}
              placeholder={FORMAT_COPY.comic.placeholder}
              onChange={(e) => onStoryIdeaChange(e.target.value)}
            />
          )}
          {ideaNotice}
        </div>
      </div>

      {gate.showing && (
        <TypedRelativeNotice
          words={relativeWords}
          onChangePhoto={gate.changePhoto}
          onContinue={() => {
            gate.continueWithout();
            handleNext(true);
          }}
        />
      )}

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <Button variant="outline" size="lg" onClick={onBack} className="w-full sm:w-auto">
          Back
        </Button>
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          onClick={() => handleNext()}
          disabled={gate.showing}
        >
          Write My Comic
        </Button>
      </div>
    </div>
  );
}
